import React, { useEffect, useState } from "react";
import {
  getLeadListsPromise,
  createLeadListPromise,
  getExcludeListsPromise,
  createExcludeListPromise,
} from "../../services/apiService";

export default function LeadsStep({ state, setState }) {
  const [lists, setLists] = useState([]);
  const [excludeLists, setExcludeLists] = useState([]);
  const [creatingList, setCreatingList] = useState(false);
  const [newListName, setNewListName] = useState("");

  useEffect(() => {
    getLeadListsPromise({ limit: 100 })
      .then((res) => setLists(res.data?.data || res.data || []))
      .catch(() => setLists([]));
    getExcludeListsPromise({ limit: 100 })
      .then((res) => setExcludeLists(res.data?.data || res.data || []))
      .catch(() => setExcludeLists([]));
  }, []);

  const createList = async () => {
    if (!newListName.trim()) return;
    setCreatingList(true);
    try {
      const res = await createLeadListPromise({ name: newListName.trim() });
      const created = res.data;
      setLists((prev) => [created, ...prev]);
      setState((prev) => ({
        ...prev,
        leadListIds: [created.id],
        leadListId: created.id,
      }));
      setNewListName("");
    } finally {
      setCreatingList(false);
    }
  };

  const createExclude = async () => {
    const name = window.prompt("Exclude list name");
    if (!name) return;
    const res = await createExcludeListPromise({ name });
    setExcludeLists((prev) => [res.data, ...prev]);
    setState((prev) => ({ ...prev, excludeListId: res.data.id }));
  };

  const toggleList = (id) => {
    setState((prev) => {
      const current = new Set(prev.leadListIds || []);
      if (current.has(id)) current.delete(id);
      else current.add(id);
      const leadListIds = Array.from(current);
      return { ...prev, leadListIds, leadListId: leadListIds[0] || null };
    });
  };

  const opts = state.excludeOptions || {};

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <label className="text-sm font-semibold text-gray-800">Campaign name</label>
        <input
          value={state.name || ""}
          onChange={(e) => setState((prev) => ({ ...prev, name: e.target.value }))}
          className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm"
          placeholder="e.g. Recruiters Tech Outreach"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-800">Lead lists</label>
          <div className="flex gap-2">
            <input
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="New list name"
              className="border rounded-lg px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={createList}
              disabled={creatingList}
              className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-sm"
            >
              Create empty list
            </button>
          </div>
        </div>
        <div className="border border-gray-200 rounded-xl divide-y max-h-64 overflow-y-auto">
          {lists.length === 0 ? (
            <div className="p-4 text-sm text-gray-400">No lead lists yet</div>
          ) : (
            lists.map((list) => {
              const selected = (state.leadListIds || []).includes(list.id);
              return (
                <label
                  key={list.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleList(list.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{list.name}</div>
                    <div className="text-xs text-gray-500">
                      {list.leadCount ?? list._count?.leads ?? 0} leads
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-800">Exclude list (optional)</label>
          <button type="button" onClick={createExclude} className="text-sm text-blue-600">
            + New exclude list
          </button>
        </div>
        <select
          value={state.excludeListId || ""}
          onChange={(e) =>
            setState((prev) => ({ ...prev, excludeListId: e.target.value || null }))
          }
          className="w-full border rounded-xl px-4 py-2.5 text-sm"
        >
          <option value="">None</option>
          {excludeLists.map((list) => (
            <option key={list.id} value={list.id}>
              {list.name} ({list.entryCount ?? 0})
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold text-gray-800">Exclude options</label>
        {[
          ["excludeOtherCampaigns", "Exclude leads from other campaigns"],
          ["excludeMessagedByOtherSenders", "Exclude leads from existing conversations"],
          ["excludeSameSenderOtherCampaigns", "Exclude leads from sender in other campaigns"],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={Boolean(opts[key])}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  excludeOptions: {
                    ...(prev.excludeOptions || {}),
                    [key]: e.target.checked,
                  },
                }))
              }
            />
            {label}
          </label>
        ))}
      </div>
    </div>
  );
}
